import { useState, useEffect, useRef, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, Check, Pause, Play } from 'lucide-react';

export interface CoachmarkStep {
  targetSelector: string;
  title: string;
  description: string;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  spotlightPadding?: number;
  durationMs?: number;
}

interface DriverCoachmarkProps {
  steps: CoachmarkStep[];
  onFinish: () => void;
  onSkip: () => void;
}

const BASE_READING_SPEED = 28;
const MIN_DURATION_MS = 4000;
const MAX_DURATION_MS = 10000;

function calcDuration(description: string): number {
  const estimated = Math.ceil(description.length / BASE_READING_SPEED) * 1000;
  return Math.max(MIN_DURATION_MS, Math.min(MAX_DURATION_MS, estimated));
}

export function DriverCoachmark({ steps, onFinish, onSkip }: DriverCoachmarkProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  const [autoPlay, setAutoPlay] = useState(true);
  const [progress, setProgress] = useState(0);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const autoTimerRef = useRef<number | null>(null);
  const progressTimerRef = useRef<number | null>(null);

  const step = steps[currentIndex];
  const stepDuration = step ? (step.durationMs || calcDuration(step.description)) : MIN_DURATION_MS;

  const updatePosition = useCallback(() => {
    if (!step) return;
    const el = document.querySelector(step.targetSelector);
    if (!el) {
      setTargetRect(null);
      return;
    }
    const rect = el.getBoundingClientRect();
    setTargetRect(rect);

    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const gap = 12;
    const tooltipWidth = Math.min(vw - 32, 300);
    const tooltipHeight = 220;

    const placement = step.placement || 'bottom';

    let top: number;
    let left: number;

    const tryBottom = () => {
      const t = rect.bottom + gap;
      const l = rect.left + rect.width / 2 - tooltipWidth / 2;
      const clampedL = Math.max(16, Math.min(l, vw - tooltipWidth - 16));
      if (t + tooltipHeight + gap > vh) return null;
      return { top: t, left: clampedL };
    };

    const tryTop = () => {
      const t = rect.top - gap - tooltipHeight;
      const l = rect.left + rect.width / 2 - tooltipWidth / 2;
      const clampedL = Math.max(16, Math.min(l, vw - tooltipWidth - 16));
      if (t < 16) return null;
      return { top: t, left: clampedL };
    };

    let pos: { top: number; left: number } | null = null;

    if (placement === 'bottom') {
      pos = tryBottom() || tryTop();
    } else if (placement === 'top') {
      pos = tryTop() || tryBottom();
    }

    if (!pos) {
      pos = {
        top: Math.max(16, Math.min(vh / 2 - tooltipHeight / 2, vh - tooltipHeight - 16)),
        left: Math.max(16, (vw - tooltipWidth) / 2),
      };
    }

    setTooltipStyle({
      position: 'fixed',
      left: `${pos.left}px`,
      top: `${pos.top}px`,
      width: `${tooltipWidth}px`,
      zIndex: 10001,
    });
  }, [step]);

  const scrollTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (step) {
      const el = document.querySelector(step.targetSelector);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
      scrollTimerRef.current = window.setTimeout(() => updatePosition(), 400);
    }
    return () => {
      if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
    };
  }, [step, updatePosition]);

  useEffect(() => {
    const onResize = () => updatePosition();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [updatePosition]);

  const clearAutoTimers = useCallback(() => {
    if (autoTimerRef.current) { clearTimeout(autoTimerRef.current); autoTimerRef.current = null; }
    if (progressTimerRef.current) { clearInterval(progressTimerRef.current); progressTimerRef.current = null; }
  }, []);

  const startAutoTimer = useCallback(() => {
    clearAutoTimers();
    if (!autoPlay) return;

    setProgress(0);
    const interval = 50;
    const totalSteps = stepDuration / interval;

    progressTimerRef.current = window.setInterval(() => {
      setProgress((prev) => Math.min(100, prev + 100 / totalSteps));
    }, interval);

    autoTimerRef.current = window.setTimeout(() => {
      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current);
        progressTimerRef.current = null;
      }
      if (currentIndex < steps.length - 1) {
        setCurrentIndex((i) => i + 1);
      } else {
        onFinish();
      }
    }, stepDuration);
  }, [autoPlay, stepDuration, currentIndex, steps.length, onFinish, clearAutoTimers]);

  useEffect(() => {
    startAutoTimer();
    return clearAutoTimers;
  }, [currentIndex, startAutoTimer, clearAutoTimers]);

  useEffect(() => {
    if (!autoPlay) {
      clearAutoTimers();
      setProgress(0);
    } else {
      startAutoTimer();
    }
    return clearAutoTimers;
  }, [autoPlay, startAutoTimer, clearAutoTimers]);

  const toggleAutoPlay = () => {
    setAutoPlay((prev) => !prev);
  };

  const goToStep = useCallback((nextIndex: number) => {
    clearAutoTimers();
    setProgress(0);
    setCurrentIndex(nextIndex);
  }, [clearAutoTimers]);

  const handleNext = () => {
    if (currentIndex < steps.length - 1) {
      goToStep(currentIndex + 1);
    } else {
      onFinish();
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      goToStep(currentIndex - 1);
    }
  };

  const handleDotClick = (index: number) => {
    goToStep(index);
  };

  const isLast = currentIndex === steps.length - 1;

  if (!step) return null;

  return (
    <>
      <div
        ref={overlayRef}
        className="fixed inset-0 z-40 bg-black/40"
        onClick={onSkip}
      />

      {targetRect && (
        <div
          className="fixed z-[10000] pointer-events-none rounded-xl border-[3px] border-blue-400 shadow-[0_0_0_4px_rgba(59,130,246,0.3),0_0_20px_rgba(59,130,246,0.2)] animate-pulse"
          style={{
            left: targetRect.left - (step.spotlightPadding ?? 8),
            top: targetRect.top - (step.spotlightPadding ?? 8),
            width: targetRect.width + (step.spotlightPadding ?? 8) * 2,
            height: targetRect.height + (step.spotlightPadding ?? 8) * 2,
            transition: 'all 0.3s ease',
          }}
        />
      )}

      <div ref={tooltipRef} style={tooltipStyle} className="fixed z-[10001]">
        <div className="rounded-2xl bg-white shadow-2xl p-4">
          {/* Progress bar */}
          {autoPlay && (
            <div className="h-1 bg-gray-100 rounded-full mb-3 overflow-hidden">
              <div
                className="h-full bg-blue-500 rounded-full transition-none"
                style={{ width: `${progress}%` }}
              />
            </div>
          )}

          <div className="flex items-start justify-between mb-2.5">
            <span className="text-[11px] font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
              {currentIndex + 1} / {steps.length}
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={toggleAutoPlay}
                className="text-gray-400 hover:text-gray-600 p-0.5"
                title={autoPlay ? 'Otomatik geçişi durdur' : 'Otomatik geçişi başlat'}
              >
                {autoPlay ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
              </button>
              <button type="button" onClick={onSkip} className="text-gray-400 hover:text-gray-600 p-0.5 -mr-1 -mt-1">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <h3 className="text-sm font-bold text-gray-900 mb-1.5 leading-snug">{step.title}</h3>
          <p className="text-xs text-gray-600 leading-relaxed mb-4">{step.description}</p>

          {/* Step dots */}
          <div className="flex items-center justify-center gap-1.5 mb-3">
            {steps.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => handleDotClick(i)}
                className={`w-2 h-2 rounded-full transition-colors ${i === currentIndex ? 'bg-blue-600' : 'bg-gray-300'}`}
              />
            ))}
          </div>

          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={handlePrev}
              disabled={currentIndex === 0}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-3.5 h-3.5" /> Geri
            </button>

            <button
              type="button"
              onClick={handleNext}
              className="flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-xl hover:bg-blue-700 transition-colors shadow-md"
            >
              {isLast ? (
                <>Tamam <Check className="w-3.5 h-3.5" /></>
              ) : (
                <>İleri <ChevronRight className="w-3.5 h-3.5" /></>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
